import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { deleteErrorCode, getErrorCatalogue, listErrorCatalogues } from '../api/errors';
import { queryKeys } from './keys';

/**
 * Error pages change only when a service uploads a catalogue, which is rare and
 * never while someone is reading one. A generous staleTime keeps the anonymous
 * reader — who arrived from one failing call and is clicking between codes —
 * from re-fetching the same catalogue on every navigation.
 */
const CATALOGUE_STALE_TIME = 5 * 60 * 1_000;

export function useErrorCatalogues() {
  return useQuery({
    queryKey: queryKeys.errorCatalogues(),
    queryFn: listErrorCatalogues,
    staleTime: CATALOGUE_STALE_TIME,
  });
}

export function useErrorCatalogue(catalogueId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.errorCatalogue(catalogueId ?? ''),
    queryFn: () => getErrorCatalogue(catalogueId!),
    enabled: Boolean(catalogueId),
    staleTime: CATALOGUE_STALE_TIME,
    // A catalogue nobody has published is a 404 and stays one until an upload.
    retry: false,
  });
}

/**
 * Every catalogue at once — for the admin registry view, which has to show the
 * whole picture, and for the index sidebar's expanded branches.
 *
 * One request per catalogue rather than a single fat endpoint: the reader-facing
 * route is the one that has to stay cheap and cacheable, and these share its
 * cache entries, so opening the admin page warms the pages themselves.
 */
export function useAllErrorCatalogues(catalogueIds: string[]) {
  return useQueries({
    queries: catalogueIds.map((catalogueId) => ({
      queryKey: queryKeys.errorCatalogue(catalogueId),
      queryFn: () => getErrorCatalogue(catalogueId),
      staleTime: CATALOGUE_STALE_TIME,
    })),
  });
}

/**
 * Deletes an error page. Invalidates both the index (counts change, and a
 * catalogue can disappear entirely when its last code goes) and the catalogue
 * itself.
 */
export function useDeleteErrorCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      catalogueId,
      code,
      force,
    }: {
      catalogueId: string;
      code: string;
      force?: boolean;
    }) => deleteErrorCode(catalogueId, code, force),
    onSuccess: (_result, { catalogueId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.errorCatalogues() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.errorCatalogue(catalogueId) });
    },
  });
}
